

import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { exportStyledExcel } from "./exportStyledExcel";
import { CircularProgress } from "@mui/material";
import { set } from "date-fns";

const AuaAuthAnalysisExcel = ({ shouldFetchFunctionList }) => {
  const [functionList, setFunctionList] = useState([]);
  const [selectedFunction, setSelectedFunction] = useState("");
  const [monthOptions, setMonthOptions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [excelData, setExcelData] = useState([]);
  const [downloadMessage, setDownloadMessage] = useState("");
  const [loadingFunctionList, setLoadingFunctionList] = useState(false);
    const [loadingSheet, setLoadingSheet] = useState(false);

  
  const hasFetchedFunctionList = useRef(false);
  const hasFetchedMonthDistrict = useRef(false);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (shouldFetchFunctionList && isInitialMount.current && !hasFetchedFunctionList.current) {
      fetchFunctions();
      isInitialMount.current = false;
    }
    
    return () => {
      
    };
  }, [shouldFetchFunctionList]);

  useEffect(() => {
    if (selectedFunction && !hasFetchedMonthDistrict.current) {
      fetchMonthAndDistrict();
      hasFetchedMonthDistrict.current = true;
    }
    
    return () => {
      hasFetchedMonthDistrict.current = false;
    };
  }, [selectedFunction]);

  const fetchFunctions = async () => {
    if (hasFetchedFunctionList.current) return;
    
    setLoadingFunctionList(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/api/auaAuth/functionList`,
        { withCredentials: true }
      );
      setFunctionList(response.data || []);
      hasFetchedFunctionList.current = true;
    } catch (err) {
      console.error("Failed to fetch function list:", err);
    } finally {
      setLoadingFunctionList(false);
    }
  };

  const fetchMonthAndDistrict = async () => {
    try {
      const monthRes = await axios.get(
        `${process.env.REACT_APP_URL}/api/auaAuth/monthAndYear`,
        {
          params: { function: selectedFunction },
          withCredentials: true,
        }
      );

      const data = monthRes.data || [];

      if (data.length > 0 && data[0].monthActive === 1) {
        const months = generateMonthYearOptions(data[0].noOfMonth);
        setMonthOptions(months);
        if (months.length > 0) {
          setSelectedMonth(months[0]);
        }
      } else {
        setMonthOptions([]);
        setSelectedMonth("");
      }

      if (data.length > 0 && data[0].distActive === 1) {
        const distRes = await axios.get(
          `${process.env.REACT_APP_URL}/api/auaAuth/district`,
          { withCredentials: true }
        );
        setDistricts(distRes.data || []);
        setSelectedDistrict("");
      } else {
        setDistricts([]);
        setSelectedDistrict("");
      }
    } catch (err) {
      console.error("Error fetching month/year or district data:", err);
    }
  };

  const generateMonthYearOptions = (noOfMonths) => {
    const today = new Date();
    const options = [];
    for (let i = 0; i < noOfMonths; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = date.toLocaleString("default", { month: "long" });
      const year = date.getFullYear();
      options.push(`${monthName} ${year}`);
    }
    return options;
  };

  const handleGetLink = async () => {
    if (!selectedFunction || !selectedMonth) {
      alert("Please select both function and month!");
      return;
    }

    const [monthName, year] = selectedMonth.split(" ");
    const month = new Date(`${monthName} 1, ${year}`).getMonth() + 1;

    let cleanName = selectedFunction.replace(/^.*\./, "").replace(/_fn$/i, "");
    cleanName = cleanName.replace(/_/g, " ").trim().toUpperCase();

    const dist =
      selectedDistrict && selectedDistrict !== ""
        ? districts.find((d) => d.distCode === selectedDistrict)
        : null;
    const districtName = dist ? dist.distNameEn.toUpperCase() : "ALL DISTRICTS";

    const msg = `${cleanName} FOR THE MONTH OF ${monthName.toUpperCase()} ${year} IN ${districtName}`;
    setDownloadMessage(msg);

    try {
      const actualTableName = selectedFunction.replace("_fn", "");
      const params = { tableName: actualTableName, month, year };
      if (selectedDistrict && selectedDistrict !== "") {
        params.distCode = selectedDistrict;
      }
      setLoadingSheet(true);

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/api/auaAuth/excelData`,
        {
          params,
          withCredentials: true,
        }
      );

      setExcelData(response.data || []);
      setLoadingSheet(false);
    } catch (err) {
      console.error("Error fetching Excel data:", err);
      alert("Error fetching Excel data");
    }
  };

  const handleDownloadExcel = async () => {
    if (!excelData.length) {
      alert("No data available to download!");
      return;
    }

    const districtLabel =
      selectedDistrict && selectedDistrict !== ""
        ? selectedDistrict
        : "AllDistricts";

    const excelFileName = `${selectedFunction}_${selectedMonth}_${districtLabel}.xlsx`;

    try {
      await exportStyledExcel(excelData, excelFileName, downloadMessage, true);
    } catch (error) {
      console.error("Error exporting Excel:", error);
      alert("Failed to export Excel file.");
    }
  };

  const isExpanded =
    selectedFunction || monthOptions.length > 0 || districts.length > 0;

  return (
    <div style={{ padding: "10px", textAlign: "center" }}>
      <table
        style={{
          width: isExpanded ? "49%" : "20%",
          transition: "width 0.5s ease",
          margin: "0 auto",
          borderCollapse: "collapse",
          boxShadow: "0px 0px 6px rgba(0,0,0,0.15)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "black", color: "white", height: "35px" }}>
            <th style={{ padding: "6px", textAlign: "center", fontSize: "16px" }}>
              Excel Download
            </th>
          </tr>
        </thead>

        <tbody>
          <tr style={{ backgroundColor: "#fafafa" }}>
            <td style={{ padding: "6px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "15px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <label style={{ fontWeight: "bold", fontSize: "14px" }}>Option:</label>
                  <select
                    value={selectedFunction}
                    onChange={(e) => {
                      setSelectedFunction(e.target.value);
                      setExcelData([]);
                      setDownloadMessage("");
                      hasFetchedMonthDistrict.current = false;
                    }}
                    disabled={loadingFunctionList}
                    style={{
                      padding: "5px",
                      borderRadius: "5px",
                      border: "1px solid #ccc",
                      minWidth: "140px",
                      height: "28px",
                      backgroundColor: loadingFunctionList ? "#f0f0f0" : "white",
                    }}
                  >
                    <option value="">-- Select Function --</option>
                    {functionList.map((fn, idx) => (
                      <option key={idx} value={fn.fnName}>
                        {fn.heading}
                      </option>
                    ))}
                  </select>
                </div>

                {monthOptions.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <label style={{ fontWeight: "bold", fontSize: "14px" }}>Month:</label>
                    <select
                      value={selectedMonth}
                      onChange={(e) => {
                        setSelectedMonth(e.target.value);
                        setExcelData([]);
                        setDownloadMessage("");
                      }}
                      style={{
                        padding: "5px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        minWidth: "130px",
                        height: "28px",
                      }}
                    >
                      <option value="">-- Select Month --</option>
                      {monthOptions.map((month, i) => (
                        <option key={i} value={month}>
                          {month}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {districts.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <label style={{ fontWeight: "bold", fontSize: "14px" }}>District:</label>
                    <select
                      value={selectedDistrict}
                      onChange={(e) => {
                        setSelectedDistrict(e.target.value);
                        setExcelData([]);
                        setDownloadMessage("");
                      }}
                      style={{
                        padding: "5px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        minWidth: "140px",
                        height: "28px",
                      }}
                    >
                      <option value="">-- All Districts --</option>
                      {districts.map((dist, i) => (
                        <option key={i} value={dist.distCode}>
                          {dist.distNameEn}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {(selectedFunction || monthOptions.length > 0 || districts.length > 0) && (
                  <button
                    onClick={handleGetLink}
                    style={{
                      backgroundColor: "black",
                      color: "white",
                      padding: "0px 19px",
                      border: "none",
                      borderRadius: "20px",
                      cursor: "pointer",
                      fontWeight: "bold",
                      fontSize: "12px",
                      height: "30px",
                    }}
                  >
                    Get Link
                  </button>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {downloadMessage && !loadingSheet && excelData.length > 0 && (
        <div style={{ marginTop: "25px" }}>
          <a
            href="#"
            onClick={handleDownloadExcel}
            style={{
              color: "blue",
              textDecoration: "underline",
              fontWeight: "bold",
              fontSize: "16px",
            }}
          >
            {downloadMessage}
          </a>
        </div>
      )}
      {loadingSheet && excelData.length === 0 && (
        <div style={{ marginTop: "25px" }} align="center">
          <span style={{ color: "blue", fontWeight: "bold", fontSize: "16px" }}>
            <CircularProgress size={20} style={{ marginRight: "10px" }} />
          </span>
        </div>
      )}

      {downloadMessage && !loadingSheet && excelData.length === 0 && (
        <div
          style={{
            color: "darkred",
            fontWeight: "bold",
            fontSize: "20px",
            paddingTop: "30px",
          }}
        >
          No Data Found
        </div>
      )}
    </div>
  );
};

export default AuaAuthAnalysisExcel;